import { ref, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';

let audioSingleton = null;

export function useMusic() {
  if (!audioSingleton) {
    audioSingleton = createAudioEngine();
  }
  return audioSingleton;
}

function createAudioEngine() {
  //Reactive state
  const volume = ref(Number(localStorage.audiovolume ?? 0.2));
  const muted = ref(localStorage.audiomuted === 'true');
  const noMusic = ref(true);

  //HTML audio player
  const audio = new Audio();
  audio.loop = true;
  audio.id = 'user-music';
  audio.style.display = 'none';
  audio.volume = volume.value;
  audio.muted = muted.value;
  
  //Helpers
  function rand(list) {
    return list ? list[list.length * Math.random() | 0] : null;
  }

  //Registers a mutation observer for an element. Upon triggering and the callback
  //returning true, observation ceases. All observers are disconnected and the array is cleared.
  //TODO: this is a total hack from rot.js and it needs to replaced with integration into the router and relevant components
  const observers = [];
  function waitUntilSpecial(selector, callback) {
    function test() {
      // Query for elements matching the specified selector
      return Array.from(document.querySelectorAll(selector)).some(callback);
    }
    function cleanup() {
      for (const o of observers)
        o.disconnect();
      observers.length = 0;
    }

    if (test()) {
      cleanup();
    } else {
      const newObserver = new MutationObserver((mutationRecords, observer) => {
        if (test())
          cleanup();
      });
      observers.push(newObserver);
      newObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  function getPromiseFromEvent(item, event) {
    return new Promise((resolve) => {
      const listener = () => {
        item.removeEventListener(event, listener);
        resolve();
      }
      item.addEventListener(event, listener);
    });
  }

  //Music functions
  function setMusic(url) {
    noMusic.value = !url;
    if (url) {
      if (audio.src !== url && audio.src !== new URL(url, document.baseURI).href) {
        audio.pause();
        audio.src = url;
      }
      playMusic();
    } else {
      audio.pause();
    }
  }

  function playMusic() {
    //Starts playing the music if it isn't muted and isn't already playing
    try {
      if (audio && !noMusic.value && !audio.muted && audio.paused) {
        console.log("Playing music");
        audio.pause();
        audio.currentTime = 0;
        audio.load();
        audio.play().catch(e => getPromiseFromEvent(window, 'click').then(playMusic));
      }
    } catch {
      console.log("Failed to play music");
      noMusic.value = true;
    }
  }

  function volumeSet(v) {
    const clamped = Math.max(0, Math.min(1, v));
    volume.value = clamped;
    audio.volume = clamped;
    localStorage.audiovolume = clamped;
  }

  function toggleMute() {
    muted.value = !muted.value;
    audio.muted = muted.value;
    localStorage.audiomuted = muted.value;
    playMusic();
  }

  //Theme application
  function applyMainTheme() {
    setMusic(null);

    //<meta name="pageMusic" content="/assets/music.ogg">
    waitUntilSpecial('meta[name="pageMusic"]', (pageMusic) => {
      setMusic(pageMusic.content);
      return true;
    });
  }

  function applyUserTheme() {
    setMusic(null);

    //Configure by post
    waitUntilSpecial(".pin", (pinnedPost) => {
      let p = pinnedPost.nextElementSibling;
      if (!p)
        return false;

      let statusBody = p.querySelector(".StatusBody");
      if (!statusBody)
        return false;

      let txtElm = statusBody.querySelector(".text");
      if (!txtElm)
        return false;

      if (txtElm.innerHTML.replace(/(<([^>]+)>)/ig, '').search(/profile them/ig) == -1)
        return false;

      const ptp = pinnedPost.nextElementSibling.querySelector(".Gallery");
      if (!ptp)
        return false;

      const musicContainers = ptp.querySelectorAll(".audio-container");
      if (musicContainers.length !== 0) {
        if (musicContainers.length === 1)
          setMusic(musicContainers[0].firstChild.src);
        else
          setMusic(rand(musicContainers).firstChild.src);
        return true;
      }
      
      const placeholderContainer = ptp.querySelector(".placeholder-container");
      if (placeholderContainer && placeholderContainer.href.endsWith(".m4a")) {
        setMusic(placeholderContainer.href);
        return true;
      } else {
        setMusic(null);
        return false;
      }
    });
  }

  function updateRoute(path) {
    const parts = path.split('/');
    switch (parts.length) {
      case 2:
        switch (parts[1]) {
          case "announcements":
          case "lists":
          case "search":
            break;

          case "about":
            applyMainTheme();
            return;

          default:
            applyUserTheme();
            return;
        }
        break;

      case 3:
        switch (parts[1]) {
          case "users":
            applyUserTheme();
            return;
        }
        break;
    }
    setMusic(null);
  }

  //Route watching
  const route = useRoute()
  watch(
    () => route.fullPath,
    async () => {
      await nextTick();
      updateRoute(window.location.pathname);
    },
    { immediate: true }
  );

  return {
    volume,
    muted,
    volumeSet,
    toggleMute
  };
}
